import React, { useEffect, useRef } from "react"
import { INK, ATOM, FLASH } from "./styles"

// 8-stage skeletal-growth animation, forward then reverse, on a loop:
//   0 aliphatic zig-zag chain (open)
//   1 six-membered ring (chain closes)
//   2 ring + one substituent bond
//   3 ring fused to a five-membered ring
//   4 + two substituents on the five-ring
//   5 two fused six-membered rings (naphthalene)
//   6 + several substituents on the bicyclic
//   7 steroid-like 6-6-5 (five-ring fused on a different/lower face)
//   8 + four substituents on the steroid
//
// One fixed pool of 17 atoms + one master bond list. Each stage sets atom
// positions and which bonds/atoms are active; transitioning interpolates
// positions and fades bonds/atoms in/out, so new rings & substituents grow in
// rather than pop. A small flash marks each bond formation.
//
// PRODUCTION NOTE: this ring geometry is hand-computed (and is fiddly — see
// the pentagon/substituent helpers below). For the live site, regenerate the
// per-stage atom coordinates with RDKit for chemically-exact 2D coordinates
// and feed them into this same interpolation loop; the animation logic itself
// is reusable as-is.
function buildMolecule(g) {
  const NS = "http://www.w3.org/2000/svg"
  const L = 22
  const H = (L * Math.sqrt(3)) / 2
  const N = 17

  function hexAt(cx, cy) {
    return [
      [cx, cy - L],
      [cx + H, cy - L / 2],
      [cx + H, cy + L / 2],
      [cx, cy + L],
      [cx - H, cy + L / 2],
      [cx - H, cy - L / 2],
    ]
  }
  const Acx = -H,
    Acy = 0
  const A = hexAt(Acx, Acy)
  const e1 = A[1],
    e2 = A[2]

  function pentagonOn(p1, p2, refPt) {
    const s = Math.hypot(p2[0] - p1[0], p2[1] - p1[1])
    const R = s / (2 * Math.sin(Math.PI / 5))
    const mid = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2]
    const ex = (p2[0] - p1[0]) / s,
      ey = (p2[1] - p1[1]) / s
    let nx = -ey,
      ny = ex
    if (refPt) {
      if ((mid[0] - refPt[0]) * nx + (mid[1] - refPt[1]) * ny < 0) {
        nx = -nx
        ny = -ny
      }
    } else if (nx < 0) {
      nx = -nx
      ny = -ny
    }
    const apoth = R * Math.cos(Math.PI / 5)
    const C = [mid[0] + nx * apoth, mid[1] + ny * apoth]
    const a1 = Math.atan2(p1[1] - C[1], p1[0] - C[0])
    const a2 = Math.atan2(p2[1] - C[1], p2[0] - C[0])
    const step = (2 * Math.PI) / 5
    const dirSign = ((((a2 - a1 + 2 * Math.PI) % (2 * Math.PI)) < Math.PI) ? -1 : 1)
    const v = []
    for (let i = 1; i <= 3; i++) {
      const a = a1 + dirSign * step * i
      v.push([C[0] + R * Math.cos(a), C[1] + R * Math.sin(a)])
    }
    return v
  }
  const pentA = pentagonOn(e1, e2, [Acx, Acy])
  const B = hexAt(Acx + 2 * H, Acy)
  const Bp = [B[0], B[1], B[2], B[3]]
  const naphC = [(Acx + (Acx + 2 * H)) / 2, 0]
  const pentS = pentagonOn(Bp[2], Bp[3], naphC)

  function subN(p, ai, n1, n2) {
    const a = p[ai]
    const m = [(p[n1][0] + p[n2][0]) / 2, (p[n1][1] + p[n2][1]) / 2]
    const dx = a[0] - m[0],
      dy = a[1] - m[1]
    const d = Math.hypot(dx, dy) || 1
    return [a[0] + (dx / d) * L, a[1] + (dy / d) * L]
  }

  function base() {
    const p = []
    for (let i = 0; i < N; i++) p[i] = i < 6 ? A[i].slice() : A[0].slice()
    return p
  }

  function S0() {
    const p = base()
    const sx = -2.5 * L,
      dx = L * Math.cos(Math.PI / 6),
      dy = L * Math.sin(Math.PI / 6)
    for (let i = 0; i < 6; i++) p[i] = [sx + i * dx, i % 2 ? dy : -dy]
    for (let i = 6; i < N; i++) p[i] = p[0].slice()
    return p
  }
  function S1() {
    return base()
  }
  function S2() {
    const p = base()
    p[13] = subN(p, 0, 1, 5)
    return p
  }
  function S3() {
    const p = base()
    p[6] = pentA[0]
    p[7] = pentA[1]
    p[8] = pentA[2]
    return p
  }
  function S4() {
    const p = S3()
    p[14] = subN(p, 6, 1, 7)
    p[15] = subN(p, 8, 7, 2)
    return p
  }
  function S5() {
    const p = base()
    p[6] = Bp[0]
    p[7] = Bp[1]
    p[8] = Bp[2]
    p[9] = Bp[3]
    return p
  }
  function S6() {
    const p = S5()
    p[13] = subN(p, 0, 1, 5)
    p[14] = subN(p, 6, 1, 7)
    p[15] = subN(p, 7, 6, 8)
    return p
  }
  function S7() {
    const p = S5()
    p[10] = pentS[0]
    p[11] = pentS[1]
    p[12] = pentS[2]
    return p
  }
  function S8() {
    const p = S7()
    p[13] = subN(p, 0, 1, 5)
    p[14] = subN(p, 4, 3, 5)
    p[15] = subN(p, 11, 10, 12)
    p[16] = subN(p, 6, 1, 7)
    return p
  }
  let stages = [S0(), S1(), S2(), S3(), S4(), S5(), S6(), S7(), S8()]

  const rA = [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 0]]
  const chain = [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5]]
  const ringB = [[1, 6], [6, 7], [7, 8], [8, 9], [9, 2]]
  const pentARing = [[1, 6], [6, 7], [7, 8], [8, 2]]
  const pentSRing = [[8, 10], [10, 11], [11, 12], [12, 9]]
  const bondSets = [
    chain.slice(),
    rA.slice(),
    rA.concat([[0, 13]]),
    rA.concat(pentARing),
    rA.concat(pentARing).concat([[6, 14], [8, 15]]),
    rA.concat(ringB),
    rA.concat(ringB).concat([[0, 13], [6, 14], [7, 15]]),
    rA.concat(ringB).concat(pentSRing),
    rA.concat(ringB).concat(pentSRing).concat([[0, 13], [4, 14], [11, 15], [6, 16]]),
  ]

  const allPairs = []
  const key = {}
  bondSets.forEach(s => {
    s.forEach(pr => {
      const a = Math.min(pr[0], pr[1]),
        b = Math.max(pr[0], pr[1]),
        k = a + "-" + b
      if (!(k in key)) {
        key[k] = allPairs.length
        allPairs.push([a, b])
      }
    })
  })
  const activeByStage = bondSets.map(s =>
    s.map(pr => key[Math.min(pr[0], pr[1]) + "-" + Math.max(pr[0], pr[1])])
  )
  function atomsUsed(i) {
    const s = {}
    bondSets[i].forEach(pr => {
      s[pr[0]] = 1
      s[pr[1]] = 1
    })
    return s
  }

  function centreUsed(p, u) {
    let mx = 0,
      my = 0,
      n = 0
    for (let i = 0; i < N; i++) {
      if (u[i]) {
        mx += p[i][0]
        my += p[i][1]
        n++
      }
    }
    mx /= n
    my /= n
    return p.map(q => [q[0] - mx, q[1] - my])
  }
  stages = stages.map((st, i) => centreUsed(st, atomsUsed(i)))

  const els = allPairs.map(() => {
    const l = document.createElementNS(NS, "line")
    l.setAttribute("stroke", INK)
    l.setAttribute("stroke-width", "2.3")
    l.setAttribute("opacity", "0")
    g.appendChild(l)
    return l
  })
  const flash = document.createElementNS(NS, "circle")
  flash.setAttribute("fill", FLASH)
  flash.setAttribute("r", "0")
  flash.setAttribute("opacity", "0")
  g.appendChild(flash)
  const atoms = []
  for (let i = 0; i < N; i++) {
    const c = document.createElementNS(NS, "circle")
    c.setAttribute("r", "3.6")
    c.setAttribute("fill", ATOM)
    c.setAttribute("opacity", "0")
    g.appendChild(c)
    atoms.push(c)
  }

  let cur = stages[0].map(p => p.slice())
  let opac = allPairs.map(() => 0)
  activeByStage[0].forEach(i => {
    opac[i] = 1
  })
  const aOp = []
  const u0 = atomsUsed(0)
  for (let i = 0; i < N; i++) aOp[i] = u0[i] ? 1 : 0

  function render() {
    for (let d = 0; d < allPairs.length; d++) {
      const a = cur[allPairs[d][0]],
        b = cur[allPairs[d][1]]
      els[d].setAttribute("x1", a[0])
      els[d].setAttribute("y1", a[1])
      els[d].setAttribute("x2", b[0])
      els[d].setAttribute("y2", b[1])
      els[d].setAttribute("opacity", opac[d].toFixed(3))
    }
    for (let j = 0; j < N; j++) {
      atoms[j].setAttribute("cx", cur[j][0])
      atoms[j].setAttribute("cy", cur[j][1])
      atoms[j].setAttribute("opacity", aOp[j].toFixed(3))
    }
  }
  render()

  function ease(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
  }
  let idx = 0,
    dir = 1
  const HOLD = 1000,
    MORPH = 1250

  let rafId = null
  let timeoutId = null
  let stopped = false

  function morph(from, to, toAct, toAtoms, done) {
    const t0 = performance.now()
    const sO = opac.slice(),
      sA = aOp.slice()
    const tO = allPairs.map((_, i) => (toAct.indexOf(i) >= 0 ? 1 : 0))
    const tA = []
    for (let i = 0; i < N; i++) tA[i] = toAtoms[i] ? 1 : 0
    function step(now) {
      if (stopped) return
      const k = Math.min(1, (now - t0) / MORPH),
        e = ease(k)
      for (let i = 0; i < N; i++) {
        cur[i][0] = from[i][0] + (to[i][0] - from[i][0]) * e
        cur[i][1] = from[i][1] + (to[i][1] - from[i][1]) * e
      }
      for (let d = 0; d < allPairs.length; d++) {
        const s = sO[d],
          tg = tO[d]
        if (tg > s) opac[d] = s + (tg - s) * Math.max(0, (e - 0.5) / 0.5)
        else if (tg < s) opac[d] = s * (1 - Math.min(1, e / 0.5))
      }
      for (let j = 0; j < N; j++) {
        const s2 = sA[j],
          t2 = tA[j]
        if (t2 > s2) aOp[j] = s2 + (t2 - s2) * Math.max(0, (e - 0.5) / 0.5)
        else if (t2 < s2) aOp[j] = s2 * (1 - Math.min(1, e / 0.5))
      }
      const fk = Math.max(0, 1 - Math.abs(k - 0.55) / 0.14)
      flash.setAttribute("r", (12 * fk).toFixed(1))
      flash.setAttribute("opacity", (0.5 * fk).toFixed(3))
      render()
      if (k < 1) {
        rafId = requestAnimationFrame(step)
      } else {
        opac = tO.slice()
        for (let i = 0; i < N; i++) aOp[i] = tA[i]
        flash.setAttribute("opacity", "0")
        done()
      }
    }
    rafId = requestAnimationFrame(step)
  }
  function cycle() {
    let ni = idx + dir
    if (ni > stages.length - 1) {
      dir = -1
      ni = idx - 1
    } else if (ni < 0) {
      dir = 1
      ni = 1
    }
    timeoutId = setTimeout(() => {
      morph(stages[idx], stages[ni], activeByStage[ni], atomsUsed(ni), () => {
        idx = ni
        cycle()
      })
    }, HOLD)
  }
  cycle()

  return () => {
    stopped = true
    if (rafId) cancelAnimationFrame(rafId)
    if (timeoutId) clearTimeout(timeoutId)
  }
}

const MoleculeGrowth = () => {
  const gRef = useRef(null)

  useEffect(() => {
    if (!gRef.current) return undefined
    // Clear in case of hot-reload re-invocation
    while (gRef.current.firstChild) gRef.current.removeChild(gRef.current.firstChild)
    const cleanup = buildMolecule(gRef.current)
    return cleanup
  }, [])

  return (
    // Wrapper stays the same footprint as the other two stages (so all three
    // tags still line up in a row); the SVG itself is drawn 30% larger and
    // simply overflows the wrapper visually, which is harmless since nothing
    // here clips overflow.
    <div style={{ width: 210, height: 180, display: "flex", alignItems: "center", justifyContent: "center", overflow: "visible" }}>
      <svg
        viewBox="0 0 240 180"
        width="260"
        height="195"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="A molecule growing through eight stages from a chain to a steroid skeleton."
      >
        <g ref={gRef} transform="translate(120 90)" strokeLinecap="round" />
      </svg>
    </div>
  )
}

export default MoleculeGrowth
